<script setup>
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../../SettingsLayout.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TemplateTableItem from './components/TemplateTableItem.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => {},
  },
});

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedTemplate = ref({});

// Dispatch get templates action
store.dispatch('messageTemplates/fetchTemplates', props.inbox.channel_id);

const templateList = computed(
  () => store.getters['messageTemplates/getTemplates']
);
const uiFlags = computed(() => getters['messageTemplates/getUIFlags'].value);

const deleteConfirmText = computed(
  () =>
    `${t('WHATSAPP_TEMPLATES.DELETE.CONFIRM.YES')} ${selectedTemplate.value.name}`
);

const deleteRejectText = computed(
  () =>
    `${t('WHATSAPP_TEMPLATES.DELETE.CONFIRM.NO')} ${selectedTemplate.value.name}`
);

const confirmDeleteMessage = computed(
  () =>
    `${t('WHATSAPP_TEMPLATES.DELETE.CONFIRM.MESSAGE')} ${selectedTemplate.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('WHATSAPP_TEMPLATES.DELETE.CONFIRM.PLACE_HOLDER', {
      templateName: selectedTemplate.value.name,
    })}`
);

const deleteInbox = async ({ name }) => {
  try {
    await store.dispatch('messageTemplates/deleteTemplate', {
      channelId: props.inbox.channel_id,
      templateName: name,
    });
    useAlert(t('WHATSAPP_TEMPLATES.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('WHATSAPP_TEMPLATES.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTemplate.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedTemplate.value);
  closeDelete();
};
const openDelete = template => {
  showDeletePopup.value = true;
  selectedTemplate.value = template;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!templateList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
    class="p-8 pt-4"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('WHATSAPP_TEMPLATES.LIST.TITLE')"
        :description="$t('WHATSAPP_TEMPLATES.LIST.DESCRIPTION')"
      >
        <template #actions>
          <router-link
            v-if="isAdmin"
            class="button nice rounded-md"
            :to="{
              name: 'settings_inbox_new_message_template',
              params: { channel_id: props.inbox.channel_id },
            }"
          >
            <fluent-icon icon="add-circle" />
            {{ $t('WHATSAPP_TEMPLATES.LIST.ADD_BUTTON') }}
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table
        class="min-w-full overflow-x-auto divide-y divide-slate-75 dark:divide-slate-700"
      >
        <thead>
          <tr class="bg-slate-50 dark:bg-slate-800">
            <th
              scope="col"
              class="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('WHATSAPP_TEMPLATES.LIST.TABLE.HEADERS.NAME') }}
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('WHATSAPP_TEMPLATES.LIST.TABLE.HEADERS.CATEGORY') }}
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('WHATSAPP_TEMPLATES.LIST.TABLE.HEADERS.LANGUAGE') }}
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-left text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              {{ $t('WHATSAPP_TEMPLATES.LIST.TABLE.HEADERS.STATUS') }}
            </th>
            <th
              scope="col"
              class="px-4 py-3 text-right text-sm font-medium text-slate-700 dark:text-slate-200"
            />
          </tr>
        </thead>
        <tbody
          class="divide-y divide-slate-25 dark:divide-slate-800 flex-1 text-slate-700 dark:text-slate-100"
        >
          <TemplateTableItem
            v-for="template in templateList"
            :key="template.id"
            :template="template"
            :channel-id="inbox.channel_id"
            @open-delete="openDelete"
          />
        </tbody>
      </table>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('WHATSAPP_TEMPLATES.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTemplate.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
